import { Signal } from 'typed-signals';
import type { App } from 'app/App';
import type { Popup } from '../../../popups/Popup';
import type { IAPProduct } from '../../../app/iap/types';
import { ShopPopupLayout } from '../../../../layouts/popups/ShopPopupLayout';
import { ShopPopupTopBarLayout } from '../../../../layouts/popups/ShopPopupTopBarLayout';
import type { ShopInventoryViewConfig } from '../../../mainMenu/shopPage/ShopInventoryView';
import { ShopInventoryView } from '../../../mainMenu/shopPage/ShopInventoryView';
import { CoinCounterView } from '../common/counters/CoinCounterView';
import { processDynamicPropsObject } from '../utils/processDynamicPropsObject';
import { getSafeAreaInset } from '../../../safearea/SafeAreaProvider';

export class ShopPopup implements Popup {
  public readonly onCloseClicked = new Signal();

  public readonly onPurchaseItem: Signal<(product: IAPProduct) => void>;

  public readonly onOffersVisible: Signal<(products: IAPProduct[]) => void>;

  public readonly onFreeGiftClaimed: Signal<() => void>;

  public readonly onFreeGiftRefreshed: Signal<() => void>;

  public readonly onFreeGiftShown: Signal<() => void>;

  public readonly onExpanded: Signal<() => void>;

  private readonly layout = new ShopPopupLayout();

  public root = this.layout.root;

  public readonly initialBounds = this.root.getLocalBounds();

  public readonly onEvent = new Signal<(event: string) => void>();

  private readonly topBar: ShopPopupTopBarLayout | null = null;

  private readonly coinCounter: CoinCounterView | null = null;

  private expanded = false;

  private readonly shopPopupTopBarHeight = 140;

  constructor(
    private readonly app: App,
    config: ShopInventoryViewConfig,
  ) {
    const { shopInventorySlot } = this.layout;

    const inventoryView = new ShopInventoryView(config).props({
      parent: shopInventorySlot,
    });

    this.onOffersVisible = inventoryView.onOffersVisible;
    this.onFreeGiftClaimed = inventoryView.onFreeGiftClaimed;
    this.onFreeGiftRefreshed = inventoryView.onFreeGiftRefreshed;
    this.onFreeGiftShown = inventoryView.onFreeGiftShown;
    this.onExpanded = inventoryView.onExpanded;
    this.onExpanded.connect(() => {
      this.expanded = true;
    });
    this.onPurchaseItem = inventoryView.onItemClicked;

    const bounds = this.root.getLocalBounds();
    bounds.width = 780;
    bounds.height = 1280;
    this.initialBounds = bounds;

    this.topBar = new ShopPopupTopBarLayout();
    this.topBar.root.props({
      parent: app.controller.overlay.view,
      alpha: 0,
    });

    this.coinCounter = new CoinCounterView(config.state, false).props({
      parent: this.topBar.coinContainerSlot,
    });

    void this.showTopBar();

    this.topBar.closeButton.onpointertap = (): void => {
      this.root.interactiveChildren = false;
      this.onCloseClicked.emit();
    };

    this.resize();
  }

  public onHidden(): void {
    if (this.topBar?.root) {
      this.topBar.root.visible = false;
    }
  }

  public onShown(): void {
    if (this.topBar?.root) {
      this.topBar.root.visible = true;
    }
  }

  public resize(): void {
    if (this.root.destroyed) return;
    if (!this.topBar) return;
    if (this.topBar.root.destroyed) return;

    const { width, height } = this.app.renderer;
    const safeArea = getSafeAreaInset();

    // Apply safe area to top bar
    this.topBar.root.props({
      x: safeArea.left,
      y: 0,
      width: width - safeArea.left - safeArea.right,
    });

    this.topBar.fullWidth.forEach((element) => {
      element.width = width - safeArea.left - safeArea.right;
    });

    const inset = safeArea.top ?? 0;

    this.topBar.shopPopupTopBar.scaleY = 1 + inset / this.shopPopupTopBarHeight;

    this.topBar.coinContainerSlot.pivot.y = -inset;
    this.topBar.storeLabel.pivot.y = -inset;
    this.topBar.closeButton.pivot.y = -inset;
    this.layout.shopInventorySlot.pivot.y = -inset;

    processDynamicPropsObject(this.topBar.storeLabel, width, height);
    processDynamicPropsObject(this.topBar.closeButton, width, height);
  }

  private async showTopBar(): Promise<void> {
    await this.topBar?.root.tweenTo({ alpha: 1 }, 200, 'linear', true).asyncOnComplete();
  }

  public isExpanded(): boolean {
    return this.expanded;
  }

  public async hideTopBar(): Promise<void> {
    await this.topBar?.root.tweenTo({ alpha: 0 }, 200, 'linear', true).asyncOnComplete();
    this.coinCounter?.destroy({ children: true });
    this.topBar?.root.destroy({ children: true });
    this.onPurchaseItem.disconnectAll();
    this.onEvent.disconnectAll();
  }
}
